import {
  ACCOUNTING_CONTRACTOR_PAYROLL_PAGE_ROUTE,
  ACCOUNTING_WAREHOUSE_REQUESTS_PAGE_ROUTE,
  BOARD_PAGE_ROUTE,
  FSM_SUBMISSIONS_PAGE_ROUTE,
  HR_CONTRACTS_PAGE_ROUTE,
  HR_HIRING_PAGE_ROUTE,
  HR_LEAVES_PAGE_ROUTE,
  LEGAL_APPLICATIONS_PAGE_ROUTE,
  PENDING_ACTIONS_PAGE_ROUTE,
  PROFILE_PAGE_ROUTE,
  REQUESTS_PAGE_ROUTE,
} from "@/shared/utils";
import type { Notification } from "../types";

export type NotificationDeepLinkType =
  | "annual_leave"
  | "unpaid_leave"
  | "medical_leave"
  | "resignation"
  | "contract_draft"
  | "job_application"
  | "amendment"
  | "service_agreement"
  | "completion_act"
  | "vehicle_rental"
  | "premises_lease"
  | "goods_supply"
  | "vehicle_handover"
  | "premises_handover"
  | "data_change"
  | "payroll"
  | "gph_payment"
  | "unit_request"
  | "task"
  | "fsm_submission";

const DEEP_LINK_TYPES: NotificationDeepLinkType[] = [
  "annual_leave", "unpaid_leave", "medical_leave", "resignation", "contract_draft",
  "job_application", "amendment", "service_agreement", "completion_act", "vehicle_rental",
  "premises_lease", "goods_supply", "vehicle_handover", "premises_handover", "data_change",
  "payroll", "gph_payment", "unit_request", "task", "fsm_submission",
];

const LEGAL_DEEP_LINK_TYPES: Set<NotificationDeepLinkType> = new Set([
  "service_agreement",
  "completion_act",
  "vehicle_rental",
  "premises_lease",
  "goods_supply",
]);

const PROFILE_DOCUMENT_CATEGORY_BY_TYPE: Partial<Record<NotificationDeepLinkType, string>> = {
  amendment: "amendment",
  contract_draft: "work_contract",
  job_application: "candidate_application",
};

function isNotificationDeepLinkType(value: string): value is NotificationDeepLinkType {
  return (DEEP_LINK_TYPES as string[]).includes(value);
}

function getDeepLinkType(notification: Notification): NotificationDeepLinkType | null {
  const type = notification.deep_link_type ?? notification.deep_link?.type ?? null;
  if (!type) return null;
  return isNotificationDeepLinkType(type) ? type : null;
}

export function getNotificationDeepLinkId(notification: Notification): number | null {
  const id = notification.deep_link?.id ?? notification.deep_link_id;
  if (id === null || id === undefined || id === "") return null;
  const parsed = Number(id);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function withDeepLinkParams(
  path: string,
  type: NotificationDeepLinkType,
  id: number | null,
  extra?: Record<string, string | number | null | undefined>,
): string {
  const params = new URLSearchParams();
  params.set("deepLinkType", type);
  if (id !== null) params.set("deepLinkId", String(id));
  for (const [key, value] of Object.entries(extra ?? {})) {
    if (value !== null && value !== undefined && value !== "") {
      params.set(key, String(value));
    }
  }
  return `${path}?${params.toString()}`;
}

function buildProfileDocumentRoute(type: NotificationDeepLinkType, id: number | null): string {
  return withDeepLinkParams(PROFILE_PAGE_ROUTE, type, id, {
    tab: "documents",
    category: PROFILE_DOCUMENT_CATEGORY_BY_TYPE[type],
    documentId: id,
  });
}

function shouldUseReviewerPage(notification: Notification): boolean {
  return notification.kind === "actionable" || notification.event_type.includes("awaiting");
}

export function buildNotificationFallbackRoute(notification: Notification): string {
  const type = getDeepLinkType(notification);
  const id = getNotificationDeepLinkId(notification);

  if (!type) return PENDING_ACTIONS_PAGE_ROUTE;

  if (type === "task") {
    return withDeepLinkParams(BOARD_PAGE_ROUTE, type, id, { taskId: id });
  }

  if (type === "gph_payment") {
    return withDeepLinkParams(ACCOUNTING_CONTRACTOR_PAYROLL_PAGE_ROUTE, type, id, { paymentId: id });
  }

  if (type === "unit_request") {
    return withDeepLinkParams(ACCOUNTING_WAREHOUSE_REQUESTS_PAGE_ROUTE, type, id, { requestId: id });
  }

  if (type === "fsm_submission") {
    return withDeepLinkParams(FSM_SUBMISSIONS_PAGE_ROUTE, type, id, { submissionId: id });
  }

  if (type === "data_change") {
    return withDeepLinkParams(PENDING_ACTIONS_PAGE_ROUTE, type, id, { requestId: id });
  }

  if (type === "job_application") {
    return shouldUseReviewerPage(notification)
      ? withDeepLinkParams(HR_HIRING_PAGE_ROUTE, type, id, { applicationId: id })
      : buildProfileDocumentRoute(type, id);
  }

  if (type === "contract_draft" || type === "amendment") {
    return shouldUseReviewerPage(notification)
      ? withDeepLinkParams(PENDING_ACTIONS_PAGE_ROUTE, type, id, { itemId: id })
      : buildProfileDocumentRoute(type, id);
  }

  if (type === "annual_leave" || type === "unpaid_leave" || type === "medical_leave") {
    return shouldUseReviewerPage(notification)
      ? withDeepLinkParams(HR_LEAVES_PAGE_ROUTE, type, id, { leaveId: id })
      : withDeepLinkParams(REQUESTS_PAGE_ROUTE, type, id, { requestId: id });
  }

  if (type === "resignation") {
    return shouldUseReviewerPage(notification)
      ? withDeepLinkParams(HR_CONTRACTS_PAGE_ROUTE, type, id, { resignationId: id })
      : withDeepLinkParams(REQUESTS_PAGE_ROUTE, type, id, { requestId: id });
  }

  if (type === "vehicle_handover" || type === "premises_handover") {
    return withDeepLinkParams(LEGAL_APPLICATIONS_PAGE_ROUTE, type, id, { actId: id });
  }

  if (LEGAL_DEEP_LINK_TYPES.has(type)) {
    return withDeepLinkParams(LEGAL_APPLICATIONS_PAGE_ROUTE, type, id, { applicationId: id });
  }

  // payroll and any other type have no dedicated screen — land on pending actions.
  return PENDING_ACTIONS_PAGE_ROUTE;
}
